import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider } from './auth';
import { BillingProvider } from './billing';
import ProtectedRoute from './components/ProtectedRoute';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import PlaceholderPage from './pages/PlaceholderPage';
import ConnectPage from './pages/ConnectPage';
import PrivacyPage from './pages/PrivacyPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import PricingPage from './pages/PricingPage';
import PricingCheckoutPage from './pages/PricingCheckoutPage';
import HelpPage from './pages/HelpPage';
import YourPrivacyPage from './pages/YourPrivacyPage';
import TermsPage from './pages/TermsPage';
import HistoryPage from './pages/HistoryPage';
import SettingsPage from './pages/SettingsPage';
import AdminShellLayout from './components/AdminShellLayout';
import AdminMonitoringPage from './pages/admin/AdminMonitoringPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminSystemPage from './pages/admin/AdminSystemPage';
import AdminClassificationPage from './pages/admin/AdminClassificationPage';

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: '16px', padding: '2rem', textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Something went wrong</h2>
          <p style={{ color: '#6b7280' }}>An unexpected error occurred. Please reload the page.</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '8px 20px', borderRadius: '6px', background: '#2563eb', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.9rem' }}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/pricing/checkout" element={<PricingCheckoutPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/your-privacy" element={<YourPrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route
        path="/app/*"
        element={
          <ProtectedRoute>
            <Routes>
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="connect" element={<ConnectPage />} />
              <Route path="subscriptions" element={<SubscriptionsPage />} />
              <Route path="invoices" element={<PlaceholderPage title="Invoices" />} />
              <Route path="accounts" element={<PlaceholderPage title="Accounts & Services" />} />
              <Route path="auto-responses" element={<PlaceholderPage title="Auto-responses" />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="history" element={<HistoryPage />} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Routes>
          </ProtectedRoute>
        }
      />
      <Route path="/admin" element={<AdminShellLayout />}>
        <Route index element={<AdminMonitoringPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="system" element={<AdminSystemPage />} />
        <Route path="classification" element={<AdminClassificationPage />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BillingProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </BillingProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
